/**
 * Admin-only state for model selection in the debug inspector.
 * Allows overriding the default model used by agents.
 *
 * Accepts simplified model IDs (e.g., "opus", "sonnet", "studio-gemini-3.5-flash") and
 * direct runtime model IDs (e.g., "openrouter/openai/gpt-5.1").
 *
 * Model selection is chat-scoped:
 * - Each chat has its own model selection stored in localStorage
 * - New chats start with the default model
 * - Selection persists across page reloads for the same chat
 */

import type { PromptVersionOption, ReasoningEffort, SkillVersionOptions, SkillVersions } from '@elara/domain/flows';

import type { AppFlowsContainer } from '#flows.js';
import type { KeyValueStore } from '#lib/key-value-store.js';

import { ReasoningEffortSchema, SkillVersionsSchema } from '@elara/domain/flows';

import { createState } from '#lib/signals-utils.js';

const STORAGE_PREFIX = 'adminSelectedModel_';
const PROMPT_STORAGE_PREFIX = 'adminSelectedPromptVersion_';
const REASONING_STORAGE_PREFIX = 'adminSelectedReasoningEffort_';
const SKILLS_STORAGE_PREFIX = 'adminSelectedSkillVersions_';

/** Model option returned from server */
export interface ModelOption {
  id: string;
  label: string;
  provider: string;
  reasoningEfforts?: ReasoningEffort[];
  defaultReasoningEffort?: ReasoningEffort;
}

function getStorageKey(chatId: string): string {
  return `${STORAGE_PREFIX}${chatId}`;
}

export function createModelState(flows: AppFlowsContainer, keyValueStore: KeyValueStore) {
  function loadModelForChat(chatId: string): string {
    if (!chatId) return '';
    return keyValueStore.get(getStorageKey(chatId)) ?? '';
  }

  function saveModelForChat(chatId: string, modelId: string): void {
    if (!chatId) return;
    if (modelId) {
      keyValueStore.set(getStorageKey(chatId), modelId);
    } else {
      keyValueStore.remove(getStorageKey(chatId));
    }
  }

  function loadPromptVersionForChat(chatId: string): number | undefined {
    if (!chatId) return;
    const stored = Number(keyValueStore.get(`${PROMPT_STORAGE_PREFIX}${chatId}`));
    return Number.isInteger(stored) && stored > 0 ? stored : undefined;
  }

  function savePromptVersionForChat(chatId: string, version: number | undefined): void {
    if (!chatId) return;
    if (version) {
      keyValueStore.set(`${PROMPT_STORAGE_PREFIX}${chatId}`, String(version));
    } else {
      keyValueStore.remove(`${PROMPT_STORAGE_PREFIX}${chatId}`);
    }
  }

  function loadReasoningEffortForChat(chatId: string): ReasoningEffort | undefined {
    if (!chatId) return;
    const parsed = ReasoningEffortSchema.safeParse(keyValueStore.get(`${REASONING_STORAGE_PREFIX}${chatId}`));
    return parsed.success ? parsed.data : undefined;
  }

  function saveReasoningEffortForChat(chatId: string, effort: ReasoningEffort | undefined): void {
    if (!chatId) return;
    if (effort) {
      keyValueStore.set(`${REASONING_STORAGE_PREFIX}${chatId}`, effort);
    } else {
      keyValueStore.remove(`${REASONING_STORAGE_PREFIX}${chatId}`);
    }
  }

  function loadSkillVersionsForChat(chatId: string): SkillVersions {
    if (!chatId) return {};
    try {
      const parsed = SkillVersionsSchema.safeParse(
        JSON.parse(keyValueStore.get(`${SKILLS_STORAGE_PREFIX}${chatId}`) ?? '{}'),
      );
      return parsed.success ? parsed.data : {};
    } catch {
      return {};
    }
  }

  function saveSkillVersionsForChat(chatId: string, versions: SkillVersions): void {
    if (!chatId) return;
    if (Object.keys(versions).length > 0) {
      keyValueStore.set(`${SKILLS_STORAGE_PREFIX}${chatId}`, JSON.stringify(versions));
    } else {
      keyValueStore.remove(`${SKILLS_STORAGE_PREFIX}${chatId}`);
    }
  }

  const state = createState({
    /** Current chat ID for scoped model selection */
    currentChatId: '',

    /** Currently selected model override for the current chat. Empty string means use agent default. */
    selectedModel: '',

    /** Whether models are being fetched from server */
    isLoadingModels: false,

    /** Available models fetched from server */
    availableModels: [] as ModelOption[],

    /** Models grouped by provider for UI display */
    modelsByProvider: {} as Record<string, ModelOption[]>,

    /** Whether models have been fetched */
    modelsLoaded: false,

    selectedPromptVersion: undefined as number | undefined,

    selectedReasoningEffort: undefined as ReasoningEffort | undefined,

    get selectedModelOption(): ModelOption | undefined {
      return this.availableModels.find((option) => option.id === this.selectedModel);
    },

    setSelectedReasoningEffort(effort: ReasoningEffort | undefined) {
      if (!this.currentChatId) return;
      this.selectedReasoningEffort = effort;
      saveReasoningEffortForChat(this.currentChatId, effort);
    },

    get reasoningEffortOverride(): ReasoningEffort | undefined {
      const efforts = this.selectedModelOption?.reasoningEfforts ?? [];
      return this.selectedReasoningEffort && efforts.includes(this.selectedReasoningEffort)
        ? this.selectedReasoningEffort
        : undefined;
    },

    promptVersions: [] as PromptVersionOption[],

    isLoadingPromptVersions: false,

    async fetchPromptVersions() {
      if (this.isLoadingPromptVersions) return;
      this.isLoadingPromptVersions = true;
      try {
        const { data } = await flows.getPromptVersions({ agent: 'concierge' });
        this.promptVersions = data.versions;
      } catch (error) {
        console.warn('[ModelState] Failed to fetch prompt versions:', error);
      } finally {
        this.isLoadingPromptVersions = false;
      }
    },

    setSelectedPromptVersion(version: number | undefined) {
      if (!this.currentChatId) {
        console.warn('[ModelState] Cannot set prompt version without a current chat');
        return;
      }
      this.selectedPromptVersion = version;
      savePromptVersionForChat(this.currentChatId, version);
    },

    get promptVersionOverride(): number | undefined {
      return this.selectedPromptVersion;
    },

    selectedSkillVersions: {} as SkillVersions,

    skillVersionOptions: [] as SkillVersionOptions[],

    isLoadingSkillVersions: false,

    async fetchSkillVersions() {
      if (this.isLoadingSkillVersions) return;
      this.isLoadingSkillVersions = true;
      try {
        const { data } = await flows.getSkillVersions({});
        this.skillVersionOptions = data.skills;
      } catch (error) {
        console.warn('[ModelState] Failed to fetch skill versions:', error);
      } finally {
        this.isLoadingSkillVersions = false;
      }
    },

    setSelectedSkillVersions(versions: SkillVersions) {
      if (!this.currentChatId) return;
      this.selectedSkillVersions = versions;
      saveSkillVersionsForChat(this.currentChatId, versions);
    },

    get skillVersionsOverride(): SkillVersions | undefined {
      return Object.keys(this.selectedSkillVersions).length > 0 ? this.selectedSkillVersions : undefined;
    },

    /** Fetches available models from server (privileged only) */
    async fetchModels() {
      if (this.modelsLoaded || this.isLoadingModels) return;

      this.isLoadingModels = true;
      try {
        const { data } = await flows.getAvailableModels({});
        this.availableModels = data.models;
        this.modelsByProvider = data.byProvider;
        this.modelsLoaded = true;
        // A pick saved before its model left the registry would fail every message; fall back to the default.
        if (this.selectedModel && data.models.length > 0 && !this.selectedModelOption) {
          this.setSelectedModel('');
        }
        console.info('[ModelState] Fetched available models:', data.models.length);
      } catch (error) {
        console.warn('[ModelState] Failed to fetch available models:', error);
      } finally {
        this.isLoadingModels = false;
      }
    },

    /** Sets the current chat and loads its model selection from localStorage */
    setCurrentChat(chatId: string) {
      if (this.currentChatId === chatId) return;
      this.currentChatId = chatId;
      this.selectedModel = loadModelForChat(chatId);
      this.selectedPromptVersion = loadPromptVersionForChat(chatId);
      this.selectedReasoningEffort = loadReasoningEffortForChat(chatId);
      this.selectedSkillVersions = loadSkillVersionsForChat(chatId);
      console.info('[ModelState] Loaded model for chat:', chatId, '→', this.selectedModel || '(default)');
    },

    /** Sets the selected model for the current chat and persists to localStorage */
    setSelectedModel(modelId: string) {
      if (!this.currentChatId) {
        console.warn('[ModelState] Cannot set model without a current chat');
        return;
      }
      this.selectedModel = modelId;
      saveModelForChat(this.currentChatId, modelId);
      if (
        this.selectedReasoningEffort &&
        !(this.selectedModelOption?.reasoningEfforts ?? []).includes(this.selectedReasoningEffort)
      ) {
        this.setSelectedReasoningEffort(undefined);
      }
      console.info('[ModelState] Model set for chat:', this.currentChatId, '→', modelId || '(default)');
    },

    /** Clears the model override (use agent default) */
    clearSelectedModel() {
      this.setSelectedModel('');
    },

    /** Returns the model ID to use, or undefined if using agent default */
    get modelOverride(): string | undefined {
      return this.selectedModel || undefined;
    },

    /** Returns a display label for the currently selected model */
    get selectedModelLabel(): string {
      if (!this.selectedModel) return 'Agent Default';
      const option = this.availableModels.find((o) => o.id === this.selectedModel);
      return option?.label ?? this.selectedModel;
    },
  });

  return state;
}

export type ModelState = ReturnType<typeof createModelState>;
